import { FormattedLocation } from "@/hooks/useLocationSearch";
import { OPTIMISTIC_PLACE_ID_PREFIX } from "./constants";
import { OfficeSuggestion } from "../_types/wizard-types";

/**
 * Canonical form for comparing user-visible location strings: trimmed and lowercased. Every "is this
 * the same place / does this match what was typed" check on the location step goes through this so
 * they can't drift apart.
 *
 * @param value - The raw string.
 * @returns The trimmed, lowercased string.
 */
export const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * Builds a placeholder `FormattedLocation` for a suggestion that is being added optimistically — shown
 * instantly while the real Places lookup runs in the background, then swapped for the resolved location
 * (or removed on failure) by the `RESOLVE_LOCATION` reducer action. The sentinel `place_id` uniquely
 * identifies the placeholder for that reconcile. City/address default to the label so the transient
 * selected-chip reads cleanly, and `pending` marks it unresolved. Even unresolved it stays functional:
 * downstream search/insert key off `city`/`formatted_address`, not coordinates.
 *
 * @param label - The suggestion's display label (e.g. "Chicago").
 * @param query - The suggestion's disambiguated query (e.g. "Chicago, IL"), used as the sentinel key.
 * @returns A pending placeholder `FormattedLocation`.
 */
export function buildOptimisticLocation(label: string, query: string): FormattedLocation {
  return {
    city: label,
    state: null,
    country: null,
    formatted_address: label,
    place_id: `${OPTIMISTIC_PLACE_ID_PREFIX}${query}`,
    coordinates: { lat: 0, lng: 0 },
    type: "city",
    pending: true,
  };
}

/**
 * Two locations are "the same" if they share a non-empty `place_id`, falling back to a
 * case-insensitive `formatted_address` match (covers pending/placeholder entries with no place id).
 *
 * @param a - First location.
 * @param b - Second location.
 * @returns True when they refer to the same place.
 */
export function locationsMatch(a: FormattedLocation, b: FormattedLocation): boolean {
  if (a.place_id && b.place_id) return a.place_id === b.place_id;
  return normalize(a.formatted_address) === normalize(b.formatted_address);
}

/**
 * Whether an office matches a typed search: by its stored name, city, or street address. Pure.
 *
 * @param office - The office to test.
 * @param query - The raw search text; normalized here, so callers pass it as typed.
 * @returns `true` when any of the office's name, city, or address contains the query.
 */
export function officeMatchesQuery(office: OfficeSuggestion, query: string): boolean {
  const needle = normalize(query);
  return [office.label, office.location.city, office.location.formatted_address].some((field) =>
    normalize(field ?? "").includes(needle)
  );
}

/**
 * Toggles a location into/out of the current list: removes it if already present, otherwise appends it
 * unless the `limit` is reached (in which case the list is returned unchanged). Pure.
 *
 * @param current - The existing formatted locations.
 * @param location - The location to toggle.
 * @param limit - Maximum number of locations allowed.
 * @returns The next formatted-locations array.
 */
export function toggleLocation(
  current: FormattedLocation[],
  location: FormattedLocation,
  limit: number
): FormattedLocation[] {
  const existing = current.find((loc) => locationsMatch(loc, location));
  if (existing) return current.filter((loc) => !locationsMatch(loc, location));
  if (current.length >= limit) return current;
  return [...current, location];
}

/**
 * Derives the `selectedLocations` string array (formatted addresses) from formatted locations. The
 * autocomplete keys its pills and removals on `formatted_address`, so these two must always agree.
 *
 * @param formatted - The formatted locations.
 * @returns The matching array of `formatted_address` strings.
 */
export function selectedFromFormatted(formatted: FormattedLocation[]): string[] {
  return formatted.map((loc) => loc.formatted_address);
}
